/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Fleet health › Versions (docs/CONTRACT-fleet-governance.md section 12.2): one row per agent version from
 * GET /v1/admin/fleet/versions with its PCs, share, ring mix, crashes and WebView2 failures per PC in 7 days, first and
 * last seen, 30 days of PCs on it and the rings it is current for; sortable, cursor paging, Export NDJSON. This is where
 * "is 0.4.12 crashing more than 0.4.11" is answered.
 */
import { useState } from 'react';
import { Layers } from 'lucide-react';
import {
  RINGS, fleetApi, fleetExportHref, fleetFailure, pctText, ringLabel, sortParam, sourcesFailedText, toggleSort,
  type FleetList, type FleetVersionRow, type SortState,
} from '../../lib/fleet';
import { EmptyState, PageHeader, Panel, Select, Sparkline, Stack, StatusChip, Table, type Column } from '../../ui';
import { count } from '../../ui/format';
import { usePagedList } from '../pulse/common';
import { ExportLink, LoadError, SourcesNote, type FleetFrame } from './parts';

/** Newest version first (the route's default). */
const DEFAULT_SORT: SortState = { key: 'version', direction: 'desc' };

function ringMix(rings: FleetVersionRow['rings']): string {
  const parts = [...RINGS, 'unknown' as const].filter((ring) => (rings?.[ring] ?? 0) > 0).map((ring) => `${ringLabel(ring)} ${count(rings[ring])}`);
  return parts.length ? parts.join(' · ') : '—';
}

export function FleetVersions({ frame }: { frame: FleetFrame }) {
  const [ring, setRing] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const filters = { ring: ring || undefined };
  const params = { ...filters, sort: sortParam(sort) };
  const list = usePagedList((cursor) => fleetApi.versions({ ...params, cursor, limit: 50 }), JSON.stringify(params));
  const sources = list.load.state === 'ready' ? (list.load.data as FleetList<FleetVersionRow>).sourcesFailed : null;

  const columns: Array<Column<FleetVersionRow>> = [
    { key: 'version', header: 'Version', sortable: true, mono: true, cell: (row) => row.version },
    { key: 'pcs', header: 'PCs', numeric: true, sortable: true, cell: (row) => count(row.pcs) },
    { key: 'sharePct', header: 'Share', numeric: true, sortable: true, cell: (row) => pctText(row.sharePct) },
    { key: 'rings', header: 'Rings', hideOnPhone: true, cell: (row) => ringMix(row.rings) },
    { key: 'crashesPerPc7d', header: 'Crashes per PC 7 d', numeric: true, sortable: true, cell: (row) => row.crashesPerPc7d.toFixed(2) },
    { key: 'webview2PerPc7d', header: 'WebView2 per PC 7 d', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => row.webview2PerPc7d.toFixed(2) },
    { key: 'firstSeen', header: 'First seen', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => row.firstSeen },
    { key: 'lastSeen', header: 'Last seen', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => row.lastSeen },
    { key: 'spark', header: '30 days', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${row.version}: PCs on it per day, last ${row.spark.length} days`} /> },
    {
      key: 'current', header: 'Current for',
      cell: (row) => (row.isCurrentFor.length ? <span className="ui-chips">{row.isCurrentFor.map((value) => <StatusChip key={value} tone="ok" plain>{ringLabel(value)}</StatusChip>)}</span> : <span className="ui-quiet">—</span>),
    },
  ];

  return (
    <>
      <PageHeader
        tabs={frame.tabs}
        activeTab={frame.activeTab}
        meta={<span>Every agent version the PCs run, with how each is doing.</span>}
        actions={(
          <>
            <Select size="sm" className="ui-w-auto" aria-label="Ring" value={ring} onChange={(event) => setRing(event.target.value)}>
              <option value="">Ring: All</option>
              {[...RINGS, 'unknown' as const].map((value) => <option key={value} value={value}>{ringLabel(value)}</option>)}
            </Select>
            <ExportLink href={fleetExportHref('versions', filters)} />
            {frame.actions}
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <SourcesNote text={sourcesFailedText(sources)} />
          <Panel flush title="Versions" description={list.load.state === 'ready' ? `${count(list.total)} version${list.total === 1 ? '' : 's'} on PCs that are not gone` : 'The agent versions the PCs run'}>
            {list.load.state === 'failed' ? <div className="ui-panel-pad"><LoadError title="The versions could not be loaded" error={list.load.error} words={(error) => fleetFailure(error)} onRetry={list.reload} retrying={list.reloading} /></div> : (
              <Table
                label="Versions"
                columns={columns}
                rows={list.items}
                rowKey={(row) => row.version}
                loading={list.load.state === 'loading'}
                skeletonRows={5}
                sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, true)) }}
                more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
                empty={<EmptyState compact icon={Layers} title="No versions yet">A version appears here once a PC reports it.</EmptyState>}
              />
            )}
            {list.moreError ? <div className="ui-panel-pad ui-panel-pad--top"><LoadError title="The next page could not be loaded" error={list.moreError} words={(error) => fleetFailure(error)} /></div> : null}
          </Panel>
        </Stack>
      </div>
    </>
  );
}
